import { useCallback, useRef, useState } from "react";
import { UploadCloud, FileText, Package } from "lucide-react";
import clsx from "clsx";

const ACCEPT = ".pdf,.zip,application/pdf,application/zip,application/x-zip-compressed";

export default function UploadZone({ onFiles, disabled }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    (filelist) => {
      const all = Array.from(filelist || []);
      const valid = all.filter((f) => {
        const n = f.name.toLowerCase();
        return n.endsWith(".pdf") || n.endsWith(".zip");
      });
      if (valid.length) onFiles(valid);
    },
    [onFiles]
  );

  return (
    <div
      data-testid="upload-zone"
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        if (!disabled) handleFiles(e.dataTransfer.files);
      }}
      className={clsx(
        "relative border-2 border-black bg-zinc-50 transition-colors",
        "px-6 py-10 md:py-14",
        drag && "bg-black text-white",
        disabled && "opacity-60 pointer-events-none"
      )}
    >
      <div className="flex flex-col items-center gap-5 text-center">
        <div
          className={clsx(
            "border-2 border-black p-3",
            drag ? "bg-white text-black" : "bg-white"
          )}
        >
          <UploadCloud className="w-7 h-7" strokeWidth={2} />
        </div>

        <div className="space-y-2">
          <p className="font-mono text-xs uppercase tracking-[0.25em] font-semibold">
            Drag &amp; drop ZIP or PDF files
          </p>
          <h2 className="font-chivo text-2xl md:text-3xl font-black tracking-tighter">
            Ingest accident reports
          </h2>
          <p className="text-sm max-w-md mx-auto opacity-80">
            We&rsquo;ll read every driver, occupant <em>and</em> non-motorist (P12). Reports with injury level
            <span className="font-mono"> 01 / 02 / 03 / 04</span> are kept — <span className="font-mono">04</span> split by ambulance transport.
          </p>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="upload-input"
        />

        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <button
            type="button"
            data-testid="upload-browse-btn"
            onClick={() => inputRef.current?.click()}
            className={clsx(
              "font-mono text-xs uppercase tracking-[0.2em] font-bold",
              "px-5 py-3 border-2 border-black",
              drag ? "bg-white text-black" : "bg-black text-white",
              "transition-transform hover:-translate-y-[2px] active:translate-y-0",
              "hover:hard-shadow"
            )}
          >
            Browse files
          </button>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] opacity-70 inline-flex items-center gap-2">
            <FileText className="w-4 h-4" /> .pdf
            <span className="opacity-40">|</span>
            <Package className="w-4 h-4" /> .zip
          </span>
        </div>
      </div>
    </div>
  );
}
